import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { KeyRound } from "lucide-react";
import { Button } from "../../../../ui/components/Button";
import { Card } from "../../../../ui/components/Card";
import { Callout } from "../../../../ui/components/Callout";
import { AuthBrand } from "../../../../ui/components/AuthBrand";
import { PasswordInput } from "../../../../ui/components/Form";
import { PasswordStrengthBar, pwScore } from "../../../../ui/components/Form/PasswordStrength";
import * as authText from "../../../../ui/components/Auth/authTypography.style.css";
import * as authApi from "../../../lib/api/auth";
import { useAuth } from "../../../lib/auth/hooks";
import { returnAfterAuth } from "../../../lib/auth/external-redirect";
import { useI18n } from "../../../lib/i18n";

export interface ResetPasswordPageProps {
  email?: string;
  token?: string;
}

export function ResetPasswordPage({ email, token }: ResetPasswordPageProps) {
  const { completeSession } = useAuth();
  const { t, tx } = useI18n();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const score = pwScore(password);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;
  const canSubmit = score >= 3 && passwordsMatch && !!email && !!token;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !token) return;
    setError(null);
    setLoading(true);
    try {
      const result = await authApi.confirmPasswordReset({ email, token, password });
      if (result.access_token && result.refresh_token && result.refresh_token_expires_at) {
        // Profile hydrates on the destination app after Continue → returnAfterAuth.
        await completeSession(
          {
            access_token: result.access_token,
            refresh_token: result.refresh_token,
            refresh_token_expires_at: result.refresh_token_expires_at,
          },
          { deferProfile: true },
        );
      }
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || t("account.resetPassword.errorFailed"));
    } finally {
      setLoading(false);
    }
  };

  if (!email || !token) {
    return (
      <Card className={authText.authCardScroll}>
        <AuthBrand />
        <div className={authText.setupStepPanel}>
          <div className={authText.stepIconCircle}>
            <KeyRound size={24} />
          </div>
          <h1 className={authText.titleLg}>{tx("account.resetPassword.invalidLinkTitle")}</h1>
          <p className={authText.subtitleCentered}>
            {tx("account.resetPassword.invalidLinkBody")}
          </p>
          <Link to="/forgot-password">
            <Button variant="solid" color="primary" fullWidth size="lg">
              {tx("account.resetPassword.requestNewLink")}
            </Button>
          </Link>
        </div>
      </Card>
    );
  }

  if (success) {
    return (
      <Card className={authText.authCardScroll}>
        <AuthBrand />
        <div className={authText.setupStepPanel}>
          <div className={authText.stepIconCircle}>
            <KeyRound size={24} />
          </div>
          <h1 className={authText.titleLg}>{tx("account.resetPassword.successTitle")}</h1>
          <p className={authText.subtitleCentered} style={{ marginBottom: 16 }}>
            {tx("account.resetPassword.successBody")}
          </p>
          <Button
            variant="solid"
            color="primary"
            fullWidth
            size="lg"
            onClick={() => {
              returnAfterAuth();
            }}
          >
            {tx("account.resetPassword.continue")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card className={authText.authCardScroll}>
      <AuthBrand />

      <div className={authText.setupStepPanel}>
        <div className={authText.stepIconCircle}>
          <KeyRound size={24} />
        </div>
        <h1 className={authText.titleLg}>{tx("account.resetPassword.title")}</h1>
        <p className={authText.subtitleCentered} style={{ marginBottom: 0 }}>
          {tx("account.resetPassword.subtitle")}
        </p>
      </div>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 24, width: "100%" }}>
        {error && <Callout variant="error">{error}</Callout>}

        <div>
          <PasswordInput
            label={t("account.resetPassword.newPasswordLabel")}
            autoComplete="new-password"
            placeholder={t("account.resetPassword.newPasswordPlaceholder")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {password && (
            <div style={{ marginTop: 8 }}>
              <PasswordStrengthBar pw={password} />
            </div>
          )}
        </div>

        <PasswordInput
          label={t("account.resetPassword.confirmPasswordLabel")}
          autoComplete="new-password"
          placeholder={t("account.resetPassword.confirmPasswordPlaceholder")}
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          error={confirmPassword && !passwordsMatch ? t("account.resetPassword.passwordsMismatch") : undefined}
          required
        />

        <Button
          type="submit"
          color="primary"
          variant="solid"
          fullWidth
          size="lg"
          loading={loading}
          disabled={!canSubmit}
        >
          {tx("account.resetPassword.setNewPassword")}
        </Button>
      </form>
    </Card>
  );
}
